import { useEffect, useMemo, useState } from "react";
import PageMeta from "../components/common/PageMeta";
import PageBreadcrumb from "../components/common/PageBreadCrumb";
import ComponentCard from "../components/common/ComponentCard";
import RegisterLoader from "../components/common/RegisterLoader";
import Label from "../components/form/Label";
import Input from "../components/form/input/InputField";
import { Modal } from "../components/ui/modal";
import { Table, TableBody, TableCell, TableHeader, TableRow } from "../components/ui/table";
import { useLanguage } from "../context/LanguageContext";
import { getLocalizedFallback } from "../utils/apiMessageI18n";
import { createUser, fetchRolesList, fetchUsersList, getFullName, UserListItem } from "../services/userService";
import { PlusIcon } from "../icons";

function formatDateDDMMYYYY(value?: string): string {
  if (!value) return "-";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return new Intl.DateTimeFormat("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(parsed);
}

export default function Users() {
  const { language } = useLanguage();
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [users, setUsers] = useState<UserListItem[]>([]);
  const [total, setTotal] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [searchName, setSearchName] = useState("");
  const [appliedSearchName, setAppliedSearchName] = useState("");
  const [roles, setRoles] = useState<string[]>([]);
  const [isRolesLoading, setIsRolesLoading] = useState(false);
  const [selectedRole, setSelectedRole] = useState("");
  const [searchRequestKey, setSearchRequestKey] = useState(0);
  const [isCreatePanelOpen, setIsCreatePanelOpen] = useState(false);
  const [createFirstName, setCreateFirstName] = useState("");
  const [createLastName, setCreateLastName] = useState("");
  const [createEmail, setCreateEmail] = useState("");
  const [createRole, setCreateRole] = useState("");
  const [isCreatingUser, setIsCreatingUser] = useState(false);
  const [createUserError, setCreateUserError] = useState("");

  const pageTitle = getLocalizedFallback(language, {
    pt: "Usuários",
    es: "Usuarios",
    en: "Users",
  });

  const searchPlaceholder = getLocalizedFallback(language, {
    pt: "Buscar por nome...",
    es: "Buscar por nombre...",
    en: "Search by name...",
  });

  const allRolesLabel = getLocalizedFallback(language, {
    pt: "Todos",
    es: "Todos",
    en: "All",
  });

  const rolePlaceholder = getLocalizedFallback(language, {
    pt: "Perfis",
    es: "Perfiles",
    en: "Roles",
  });

  const createUserLabel = getLocalizedFallback(language, {
    pt: "Criar usuário",
    es: "Crear usuario",
    en: "Create user",
  });

  const createPanelTitle = createUserLabel;

  const firstNameLabel = getLocalizedFallback(language, {
    pt: "Nome",
    es: "Nombre",
    en: "First name",
  });
  const lastNameLabel = getLocalizedFallback(language, {
    pt: "Sobrenome",
    es: "Apellido",
    en: "Last name",
  });
  const emailLabel = getLocalizedFallback(language, {
    pt: "E-mail",
    es: "Correo",
    en: "Email",
  });
  const createRoleLabel = getLocalizedFallback(language, {
    pt: "Perfil",
    es: "Perfil",
    en: "Role",
  });

  const createRolePlaceholder = getLocalizedFallback(language, {
    pt: "Selecione um perfil",
    es: "Seleccione un perfil",
    en: "Select a role",
  });

  const createPanelDescription = getLocalizedFallback(language, {
    pt: "Preencha os dados do novo usuário.",
    es: "Complete los datos del nuevo usuario.",
    en: "Fill in the new user details.",
  });

  const closePanelLabel = getLocalizedFallback(language, {
    pt: "Fechar",
    es: "Cerrar",
    en: "Close",
  });

  const saveUserLabel = getLocalizedFallback(language, {
    pt: "Salvar alterações",
    es: "Guardar cambios",
    en: "Save changes",
  });

  const creatingUserLabel = getLocalizedFallback(language, {
    pt: "Salvando...",
    es: "Guardando...",
    en: "Saving...",
  });

  const rolesLoadingLabel = getLocalizedFallback(language, {
    pt: "Carregando perfis...",
    es: "Cargando perfiles...",
    en: "Loading roles...",
  });

  const rolesEmptyLabel = getLocalizedFallback(language, {
    pt: "Nenhum perfil encontrado",
    es: "No se encontraron perfiles",
    en: "No roles found",
  });

  const nameHeader = getLocalizedFallback(language, {
    pt: "Nome",
    es: "Nombre",
    en: "Name",
  });
  const emailHeader = getLocalizedFallback(language, {
    pt: "E-mail",
    es: "Correo",
    en: "Email",
  });
  const roleHeader = getLocalizedFallback(language, {
    pt: "Perfil",
    es: "Rol",
    en: "Role",
  });
  const createdAtHeader = getLocalizedFallback(language, {
    pt: "Criado em",
    es: "Creado en",
    en: "Created at",
  });

  const emptyLabel = getLocalizedFallback(language, {
    pt: "Nenhum usuário encontrado.",
    es: "No se encontraron usuarios.",
    en: "No users found.",
  });

  const prevLabel = getLocalizedFallback(language, {
    pt: "Anterior",
    es: "Anterior",
    en: "Previous",
  });
  const nextLabel = getLocalizedFallback(language, {
    pt: "Próximo",
    es: "Siguiente",
    en: "Next",
  });

  const pageInfo = useMemo(() => {
    if (total === null) {
      return getLocalizedFallback(language, {
        pt: `Página ${page}`,
        es: `Página ${page}`,
        en: `Page ${page}`,
      });
    }

    const totalPages = Math.max(1, Math.ceil(total / limit));
    return getLocalizedFallback(language, {
      pt: `Página ${page} de ${totalPages}`,
      es: `Página ${page} de ${totalPages}`,
      en: `Page ${page} of ${totalPages}`,
    });
  }, [language, limit, page, total]);

  const canGoPrev = page > 1;
  const canGoNext = total === null ? users.length === limit : page * limit < total;
  const canCreateUser =
    !isCreatingUser &&
    createFirstName.trim().length > 0 &&
    createLastName.trim().length > 0 &&
    createEmail.trim().length > 0 &&
    createRole.trim().length > 0;

  useEffect(() => {
    const loadRoles = async () => {
      setIsRolesLoading(true);
      try {
        const list = await fetchRolesList();
        setRoles(list);
      } catch {
        setRoles([]);
      } finally {
        setIsRolesLoading(false);
      }
    };

    void loadRoles();
  }, []);

  useEffect(() => {
    const loadUsers = async () => {
      setIsLoading(true);
      setErrorMessage("");
      try {
        const result = await fetchUsersList({
          page,
          limit,
          name: appliedSearchName,
          role: selectedRole,
        });
        setUsers(result.users);
        setTotal(result.total);
      } catch (error) {
        setUsers([]);
        setTotal(null);
        setErrorMessage(error instanceof Error ? error.message : "Erro ao carregar usuários.");
      } finally {
        setIsLoading(false);
      }
    };

    void loadUsers();
  }, [appliedSearchName, limit, page, searchRequestKey, selectedRole]);

  return (
    <>
      <PageMeta title={`${pageTitle} | Gerify`} description={pageTitle} />
      {(isLoading || isCreatingUser) && <RegisterLoader />}
      <PageBreadcrumb pageTitle={pageTitle} />
      <ComponentCard
        headerLeft={
          <div className="flex w-full flex-wrap items-center gap-2 sm:flex-nowrap sm:w-[620px]">
            <Input
              type="text"
              value={searchName}
              onChange={(e) => setSearchName(e.target.value)}
              placeholder={searchPlaceholder}
              className="h-10"
            />
            <div className="relative w-full sm:w-56">
              <select
                value={selectedRole}
                onChange={(e) => {
                  setPage(1);
                  setSelectedRole(e.target.value);
                  setSearchRequestKey((prev) => prev + 1);
                }}
                className="h-10 w-full appearance-none rounded-lg border border-gray-300 bg-transparent px-4 text-sm shadow-theme-xs text-gray-800 placeholder:text-gray-400 focus:border-brand-300 focus:outline-none focus:ring focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30 dark:focus:border-brand-800"
                aria-label={rolePlaceholder}
                title={rolePlaceholder}
              >
                <option value="">{allRolesLabel}</option>
                {roles.map((role) => (
                  <option key={role} value={role}>
                    {role}
                  </option>
                ))}
              </select>
              <svg
                className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 fill-gray-500 dark:fill-gray-400"
                viewBox="0 0 20 20"
              >
                <path d="M5.25 7.5L10 12.25L14.75 7.5" />
              </svg>
            </div>
            <button
              type="button"
              disabled={isLoading}
              onClick={() => {
                setPage(1);
                setAppliedSearchName(searchName.trim());
                setSearchRequestKey((prev) => prev + 1);
              }}
              aria-label={searchPlaceholder}
              title={searchPlaceholder}
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-brand-500 text-white hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <svg
                className="fill-white"
                width="18"
                height="18"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M3.04175 9.37363C3.04175 5.87693 5.87711 3.04199 9.37508 3.04199C12.8731 3.04199 15.7084 5.87693 15.7084 9.37363C15.7084 12.8703 12.8731 15.7053 9.37508 15.7053C5.87711 15.7053 3.04175 12.8703 3.04175 9.37363ZM9.37508 1.54199C5.04902 1.54199 1.54175 5.04817 1.54175 9.37363C1.54175 13.6991 5.04902 17.2053 9.37508 17.2053C11.2674 17.2053 13.003 16.5344 14.357 15.4176L17.177 18.238C17.4699 18.5309 17.9448 18.5309 18.2377 18.238C18.5306 17.9451 18.5306 17.4703 18.2377 17.1774L15.418 14.3573C16.5365 13.0033 17.2084 11.2669 17.2084 9.37363C17.2084 5.04817 13.7011 1.54199 9.37508 1.54199Z"
                  fill=""
                />
              </svg>
            </button>
          </div>
        }
        headerRight={
          <button
            type="button"
            onClick={() => {
              setCreateFirstName("");
              setCreateLastName("");
              setCreateEmail("");
              setCreateRole("");
              setCreateUserError("");
              setIsCreatePanelOpen(true);
            }}
            className="inline-flex h-10 items-center gap-2 rounded-lg bg-brand-500 px-4 text-sm font-medium text-white hover:bg-brand-600"
          >
            <PlusIcon className="h-4 w-4 fill-white" />
            <span>{createUserLabel}</span>
          </button>
        }
      >
        {errorMessage && (
          <p className="text-sm text-error-500" role="alert">
            {errorMessage}
          </p>
        )}

        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-white/[0.05] dark:bg-white/[0.03]">
          <div className="max-w-full overflow-x-auto">
            <div className="min-w-[900px]">
              <Table>
                <TableHeader className="border-b border-gray-100 dark:border-white/[0.05]">
                  <TableRow>
                    <TableCell
                      isHeader
                      className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400"
                    >
                      {nameHeader}
                    </TableCell>
                    <TableCell
                      isHeader
                      className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400"
                    >
                      {emailHeader}
                    </TableCell>
                    <TableCell
                      isHeader
                      className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400"
                    >
                      {roleHeader}
                    </TableCell>
                    <TableCell
                      isHeader
                      className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400"
                    >
                      {createdAtHeader}
                    </TableCell>
                  </TableRow>
                </TableHeader>

                <TableBody className="divide-y divide-gray-100 dark:divide-white/[0.05]">
                  {users.length === 0 ? (
                    <TableRow>
                      <TableCell className="px-5 py-6 text-gray-500 text-theme-sm dark:text-gray-400">
                        {emptyLabel}
                      </TableCell>
                      <TableCell className="px-5 py-6"> </TableCell>
                      <TableCell className="px-5 py-6"> </TableCell>
                      <TableCell className="px-5 py-6"> </TableCell>
                    </TableRow>
                  ) : (
                    users.map((user, idx) => {
                      const name = getFullName(user) || "-";
                      return (
                        <TableRow key={user.id || `${user.email || "user"}-${idx}`}>
                          <TableCell className="px-5 py-4 text-start">
                            <span className="block font-medium text-gray-800 text-theme-sm dark:text-white/90">
                              {name}
                            </span>
                          </TableCell>
                          <TableCell className="px-5 py-4 text-gray-500 text-start text-theme-sm dark:text-gray-400">
                            {user.email || "-"}
                          </TableCell>
                          <TableCell className="px-5 py-4 text-gray-500 text-start text-theme-sm dark:text-gray-400">
                            {user.role || "-"}
                          </TableCell>
                          <TableCell className="px-5 py-4 text-gray-500 text-start text-theme-sm dark:text-gray-400">
                            {formatDateDDMMYYYY(user.created_at)}
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3">
          <span className="text-sm text-gray-500 dark:text-gray-400">{pageInfo}</span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              disabled={!canGoPrev || isLoading}
              onClick={() => setPage((prev) => Math.max(1, prev - 1))}
              className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-white/5"
            >
              {prevLabel}
            </button>
            <button
              type="button"
              disabled={!canGoNext || isLoading}
              onClick={() => setPage((prev) => prev + 1)}
              className="rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {nextLabel}
            </button>
          </div>
        </div>
      </ComponentCard>

      <Modal
        isOpen={isCreatePanelOpen}
        onClose={() => setIsCreatePanelOpen(false)}
        className="max-w-[720px] p-6 lg:p-8"
      >
        <h4 className="mb-2 text-xl font-semibold text-gray-800 dark:text-white/90">
          {createPanelTitle}
        </h4>
        <p className="mb-6 text-sm text-gray-500 dark:text-gray-400">{createPanelDescription}</p>

        <h5 className="mb-4 text-base font-semibold text-gray-800 dark:text-white/90">
          {getLocalizedFallback(language, {
            pt: "Informações pessoais",
            es: "Información personal",
            en: "Personal information",
          })}
        </h5>

        <div className="space-y-5">
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <div>
              <Label htmlFor="create-first-name">{firstNameLabel}</Label>
              <Input
                id="create-first-name"
                type="text"
                value={createFirstName}
                onChange={(e) => setCreateFirstName(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="create-last-name">{lastNameLabel}</Label>
              <Input
                id="create-last-name"
                type="text"
                value={createLastName}
                onChange={(e) => setCreateLastName(e.target.value)}
              />
            </div>
          </div>

          <div>
            <Label htmlFor="create-email">{emailLabel}</Label>
            <Input
              id="create-email"
              type="email"
              value={createEmail}
              onChange={(e) => setCreateEmail(e.target.value)}
            />
          </div>

          <div>
            <Label htmlFor="create-role">{createRoleLabel}</Label>
            <div className="relative">
              <select
                id="create-role"
                value={createRole}
                onChange={(e) => setCreateRole(e.target.value)}
                className="h-11 w-full appearance-none rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm shadow-theme-xs text-gray-800 placeholder:text-gray-400 focus:border-brand-300 focus:outline-none focus:ring focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30 dark:focus:border-brand-800"
              >
                <option value="">
                  {isRolesLoading
                    ? rolesLoadingLabel
                    : roles.length === 0
                      ? rolesEmptyLabel
                      : createRolePlaceholder}
                </option>
                {roles.map((role) => (
                  <option key={role} value={role}>
                    {role}
                  </option>
                ))}
              </select>
              <svg
                className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 fill-gray-500 dark:fill-gray-400"
                viewBox="0 0 20 20"
              >
                <path d="M5.25 7.5L10 12.25L14.75 7.5" />
              </svg>
            </div>
          </div>

          {createUserError && (
            <p className="text-sm text-error-500" role="alert">
              {createUserError}
            </p>
          )}

          <div className="mt-6 flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={() => setIsCreatePanelOpen(false)}
              className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-white/5"
            >
              {closePanelLabel}
            </button>
            <button
              type="button"
              disabled={!canCreateUser}
              onClick={async () => {
                if (!canCreateUser) return;
                setIsCreatingUser(true);
                setCreateUserError("");
                try {
                  await createUser({
                    first_name: createFirstName.trim(),
                    last_name: createLastName.trim(),
                    email: createEmail.trim(),
                    role_name: createRole.trim(),
                  });
                  setIsCreatePanelOpen(false);
                  setCreateFirstName("");
                  setCreateLastName("");
                  setCreateEmail("");
                  setCreateRole("");
                  setPage(1);
                  setSearchRequestKey((prev) => prev + 1);
                } catch (error) {
                  setCreateUserError(
                    error instanceof Error ? error.message : "Não foi possível criar usuário."
                  );
                } finally {
                  setIsCreatingUser(false);
                }
              }}
              className="rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {isCreatingUser ? creatingUserLabel : saveUserLabel}
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
}

